import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Loading from "../common/Loading";
import { ACCENT, PrimaryButton, inputClass } from "./shared";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";
import DocumentSlot from "./DocumentSlot";
import { WALLET_MESSAGES, walletApi, walletErrorMessage } from "./walletApi";
import { formatDate } from "./walletFormat";

// Artist Studio > Music Wallet > Tax information: where "Upload W-9" / "Upload W-8BEN" on the wallet leads. The artist
// uploads the completed form; that only puts it in review. The wallet shows Tax: Complete once the review team verifies it
// (the server never marks it complete because of an upload). There is no screen for this in the Claude design.
const BLANK_FORM = {
  w9: { name: "W-9", who: "U.S. persons", url: "https://www.irs.gov/pub/irs-pdf/fw9.pdf" },
  w8ben: { name: "W-8BEN", who: "non-U.S. persons", url: "https://www.irs.gov/pub/irs-pdf/fw8ben.pdf" },
  w8bene: { name: "W-8BEN-E", who: "non-U.S. companies", url: "https://www.irs.gov/pub/irs-pdf/fw8bene.pdf" },
};

// The kinds of tax ID each form accepts. A W-9 needs a U.S. one; a W-8BEN / W-8BEN-E may add a foreign (or U.S.) one, optional.
const ID_KINDS = {
  w9: [["ssn", "Social Security number"], ["itin", "ITIN"], ["ein", "Employer ID number (EIN)"]],
  w8ben: [["foreign", "Tax ID in your country"], ["ssn", "Social Security number"], ["itin", "ITIN"], ["ein", "Employer ID number (EIN)"]],
  w8bene: [["foreign", "Tax ID in your country"], ["ein", "Employer ID number (EIN)"]],
};
const ID_HELP = { ssn: "9 digits, for example 123-45-6789", itin: "9 digits starting with 9, for example 912-70-1234", ein: "9 digits, for example 12-3456789", foreign: "4 to 30 letters or numbers" };
export const taxIdRequired = (formKey) => formKey === "w9";

function TaxBody() {
  const [state, setState] = useState({ status: "loading", tax: null, error: "" });
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [replacing, setReplacing] = useState(false);
  const [chosen, setChosen] = useState("");
  const [idKind, setIdKind] = useState("");
  const [idValue, setIdValue] = useState("");
  const [idError, setIdError] = useState("");

  const load = useCallback(() => {
    setState({ status: "loading", tax: null, error: "" });
    walletApi.taxForm().then(
      (tax) => setState({ status: "ready", tax, error: "" }),
      (e) => setState({ status: "error", tax: null, error: walletErrorMessage(e, WALLET_MESSAGES.taxLoad) })
    );
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (saving) return;
    const form = chosen || state.tax.form;
    const typed = idValue.trim();
    // A number already on file for this same form is kept when the box is left empty.
    const kept = Boolean(state.tax.tax_id) && form === state.tax.form;
    if (taxIdRequired(form) && !typed && !kept) { setIdError("Enter your U.S. tax ID (Social Security number, ITIN or Employer ID number)."); return; }
    if (!file?.url) { setFileError(`Upload your completed ${(BLANK_FORM[form] || {}).name || state.tax.form_label}.`); return; }
    setSaving(true);
    setSubmitError("");
    setIdError("");
    try {
      const tax = await walletApi.submitTaxForm({ document_url: file.url, tax_form: form, ...(typed ? { tax_id: typed, tax_id_type: idKind || (ID_KINDS[form] || ID_KINDS.w8ben)[0][0] } : {}) });
      setState({ status: "ready", tax, error: "" });
      setFile(null);
      setIdValue("");
      setReplacing(false);
    } catch (e) {
      const code = e?.response?.data?.code;
      if (code === "TAX_ID_INVALID" || code === "TAX_ID_REQUIRED") setIdError(walletErrorMessage(e, WALLET_MESSAGES.taxSubmit));
      else setSubmitError(walletErrorMessage(e, WALLET_MESSAGES.taxSubmit));
    } finally {
      setSaving(false);
    }
  };

  if (state.status === "loading") return <Loading />;
  if (state.status === "error") {
    return (
      <div className="rounded-2xl bg-[#fff4ea] p-6" role="alert">
        <p className="text-[15px] font-semibold text-[#b8560f]">{state.error}</p>
        <button type="button" onClick={load} className="mt-3 rounded-full bg-[#b8560f] px-5 py-2 text-sm font-semibold text-white">Try again</button>
      </div>
    );
  }

  const tax = state.tax;
  const formKey = chosen || tax.form;
  const blank = BLANK_FORM[formKey] || BLANK_FORM.w8ben;
  const kinds = ID_KINDS[formKey] || ID_KINDS.w8ben;
  const review = tax.review_status;
  const showUpload = review !== "verified" && (review !== "submitted" || replacing);

  return (
    <div>
      <Link to="/artist-studio/wallet" className="text-sm font-semibold text-gray-600 hover:underline">← Back to Music Wallet</Link>
      <h1 className="mt-3 text-[28px] font-bold text-[#14261c]">Tax information</h1>
      <p className="mt-1 text-[15px] text-gray-600">
        Payouts are switched on after we have checked your {tax.form_label} ({blank.who}).
      </p>

      {review === "verified" && (
        <div className="mt-6 rounded-2xl bg-green-50 p-5" role="status" data-testid="tax-verified">
          <p className="text-[15px] font-semibold text-green-800">Your {tax.form_label} is verified.</p>
          {tax.verified_at && <p className="mt-1 text-sm text-green-800/80">Verified on {formatDate(tax.verified_at)}.</p>}
          {tax.tax_id && <p className="mt-1 text-sm text-green-800/80" data-testid="tax-id-on-file">Tax ID on file: {tax.tax_id.type_label} {tax.tax_id.masked}</p>}
          {tax.expires_on && <p className="mt-1 text-sm text-green-800/80" data-testid="tax-expires">Valid until {formatDate(tax.expires_on)}.</p>}
          {tax.expiring_soon && <p className="mt-2 text-sm font-semibold text-[#b8560f]" data-testid="tax-expiring">It ends in {tax.days_left} {tax.days_left === 1 ? "day" : "days"}. Send a new form after it ends so your payouts are not paused.</p>}
        </div>
      )}
      {review === "expired" && (
        <div className="mt-6 rounded-2xl bg-red-50 p-5" role="alert" data-testid="tax-expired">
          <p className="text-[15px] font-semibold text-red-700">Your {tax.form_label} has expired. Payouts are paused until you send a new one and it is verified.</p>
        </div>
      )}
      {review === "submitted" && (
        <div className="mt-6 rounded-2xl bg-[#fff4ea] p-5" role="status" data-testid="tax-submitted">
          <p className="text-[15px] font-semibold text-[#b8560f]">We received your {tax.form_label} and are reviewing it.</p>
          <p className="mt-1 text-sm text-[#b8560f]/80">
            {tax.submitted_at ? `Sent on ${formatDate(tax.submitted_at)}. ` : ""}The wallet changes to Tax: Complete once it is verified.
          </p>
          {tax.tax_id && <p className="mt-1 text-sm text-[#b8560f]/80" data-testid="tax-id-on-file">Tax ID on file: {tax.tax_id.type_label} {tax.tax_id.masked}</p>}
          {!replacing && <button type="button" onClick={() => setReplacing(true)} className="mt-3 text-sm font-semibold underline text-[#b8560f]">Upload a different file</button>}
        </div>
      )}
      {review === "rejected" && (
        <div className="mt-6 rounded-2xl bg-red-50 p-5" role="alert" data-testid="tax-rejected">
          <p className="text-[15px] font-semibold text-red-700">Your last {tax.form_label} could not be accepted. Please upload it again.</p>
        </div>
      )}

      {showUpload && (
        <div className="mt-6 max-w-xl rounded-2xl border border-gray-200 bg-white p-6">
          <p className="mb-2 text-sm font-semibold text-gray-800">Choose the form for your tax residency</p>
          <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Tax form">
            {Object.entries(BLANK_FORM).map(([key, f]) => (
              <button
                key={key}
                type="button"
                aria-pressed={formKey === key}
                onClick={() => { setChosen(key); setFile(null); setFileError(""); setIdKind(""); setIdValue(""); setIdError(""); }}
                className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${formKey === key ? "text-white" : "border-gray-300 text-gray-700"}`}
                style={formKey === key ? { backgroundColor: ACCENT, borderColor: ACCENT } : undefined}
                data-testid={`tax-form-${key}`}
              >
                {f.name} <span className="font-normal opacity-80">({f.who})</span>
              </button>
            ))}
          </div>
          <div className="mb-5" data-testid="tax-id-section">
            <label htmlFor="tax-id-kind" className="block text-sm font-semibold text-gray-800">
              Tax ID {taxIdRequired(formKey) ? <span className="text-red-600">*</span> : <span className="font-normal text-gray-500">(optional)</span>}
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              <select
                id="tax-id-kind"
                aria-label="Kind of tax ID"
                value={idKind || kinds[0][0]}
                onChange={(e) => { setIdKind(e.target.value); setIdError(""); }}
                className={inputClass(false)}
                style={{ maxWidth: 240 }}
                disabled={saving}
                data-testid="tax-id-kind"
              >
                {kinds.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <input
                type="text"
                aria-label="Tax ID"
                value={idValue}
                onChange={(e) => { setIdValue(e.target.value); setIdError(""); }}
                placeholder={tax.tax_id && formKey === tax.form ? `On file: ${tax.tax_id.masked}. Leave empty to keep it.` : ID_HELP[idKind || kinds[0][0]]}
                autoComplete="off"
                inputMode="text"
                maxLength={30}
                className={inputClass(Boolean(idError))}
                style={{ maxWidth: 280 }}
                disabled={saving}
                data-testid="tax-id-value"
              />
            </div>
            {idError && <p className="mt-1 text-sm text-red-600" role="alert" data-testid="tax-id-error">{idError}</p>}
            <p className="mt-1 text-xs text-gray-500">We store it encrypted and only show the last four characters. It must match your form.</p>
          </div>
          <p className="text-sm text-gray-600">
            1. Fill in and sign the {blank.name}.{" "}
            <a href={blank.url} target="_blank" rel="noopener noreferrer" className="font-semibold underline" style={{ color: ACCENT }}>Get a blank {blank.name}</a>
          </p>
          <p className="mt-1 mb-4 text-sm text-gray-600">2. Upload a clear scan or photo of the completed form.</p>
          <DocumentSlot
            label={`Completed ${blank.name}`}
            required
            value={file}
            onChange={(f) => { setFile(f); setFileError(""); setSubmitError(""); }}
            error={fileError}
            disabled={saving}
          />
          {submitError && <p className="mt-3 text-sm text-red-600" role="alert" data-testid="tax-submit-error">{submitError}</p>}
          <div className="mt-5 flex items-center gap-3">
            <PrimaryButton onClick={submit} disabled={saving} data-testid="tax-submit">{saving ? "Sending…" : "Send for review"}</PrimaryButton>
            {replacing && <button type="button" onClick={() => { setReplacing(false); setFile(null); setFileError(""); }} className="text-sm text-gray-500 underline">Cancel</button>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TaxForm() {
  const account = useStudioAccount();
  return (
    <StudioGate account={account} active="wallet" heading="" tabs={[]} maxWidth="max-w-4xl">
      <TaxBody />
    </StudioGate>
  );
}
